import { useForm, FieldData, Box, SwitchIndicator, TextField, MaterialSymbol, withStyles, BodyText } from 'vopay';
import { ChangeEvent, useImperativeHandle, useMemo, useEffect, Ref, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';
import { Title, UtilButton } from 'UI';
import CollapsibleList from '../../Users/UserPermissions/CollapsibleList';
import styles from './AccountPermissionsForm.module.scss';

const classes = withStyles(styles);

type PermissionFormValues = Record<string, string | boolean>;

type AccountPermissionsFormProps = {
  permissionRows: Record<string, vp.PartnerAccountPermissionResponse[]>;
  onSave: (values: PermissionFormValues) => void;
  ref: Ref<AccountPermissionsFormRef>;
  onFormStateChange: (hasChanges: boolean) => void;
  onSearchChange: (isSearching: boolean) => void;
  isReadOnly?: boolean;
};

export type AccountPermissionsFormRef = {
  enableAll: () => void;
};

type ToggleProps = {
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
};

const Toggle: FC<ToggleProps> = ({ name, checked, onChange, disabled = false }) => (
  <>
    <SwitchIndicator checked={checked} disabled={disabled} />
    <input
      className={classes('hiddenCheckbox')}
      id={`checkbox-${name}`}
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  </>
);

function buildInitialValues(
  permissionRows: Record<string, vp.PartnerAccountPermissionResponse[]>,
): PermissionFormValues {
  const values: PermissionFormValues = {};
  Object.values(permissionRows)
    .flat()
    .forEach(({ Permission, Value }) => {
      values[Permission] = Value as string | boolean;
    });
  return values;
}

function buildPermissionsConfig(
  permissionRows: Record<string, vp.PartnerAccountPermissionResponse[]>,
  isReadOnly: boolean,
): Record<string, FieldData<PermissionFormValues>[]> {
  const config: Record<string, FieldData<PermissionFormValues>[]> = {};

  Object.entries(permissionRows).forEach(([sectionName, permissions]) => {
    const fields: FieldData<PermissionFormValues>[] = permissions.flatMap((permission) => {
      const { Permission, Label, Type } = permission;

      if (!['boolean', 'number', 'string', 'text'].includes(Type)) return [];

      const isCurrency = Type === 'number' || Permission.toLowerCase().includes('amount');

      return [
        {
          fieldName: Permission,
          label: () => Label,
          getInputState: () => 'optional',
          showField: () => true,
          ...(isCurrency && {
            serializeField: (values: PermissionFormValues) => {
              const val = values[Permission];
              return typeof val === 'string' ? vp.sanitizeToNumeric(val) : val;
            },
          }),
          renderField: ({ getFormikData }) => {
            if (Type === 'boolean') {
              const fieldData = getFormikData({});
              return (
                <Toggle
                  name={fieldData.name}
                  checked={Boolean(fieldData.value)}
                  onChange={fieldData.onChange}
                  disabled={isReadOnly}
                />
              );
            }
            const fieldData = getFormikData({});
            return (
              <TextField
                {...fieldData}
                id={fieldData.name}
                variant="outlineShadow"
                preset={isCurrency ? 'currency' : undefined}
                value={String(fieldData.value ?? '')}
                disabled={isReadOnly}
              />
            );
          },
        },
      ] as FieldData<PermissionFormValues>[];
    });

    if (fields.length > 0) {
      config[sectionName] = fields;
    }
  });

  return config;
}

const formatSectionName = (section: string): string =>
  section
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const AccountPermissionsForm: FC<AccountPermissionsFormProps> = ({
  permissionRows,
  onSave,
  onFormStateChange,
  onSearchChange,
  ref,
  isReadOnly = false,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Permissions.Form' });
  const [searchTerm, setSearchTerm] = useState('');

  const initialValues = useMemo(() => buildInitialValues(permissionRows), []);
  const permissionsConfig = useMemo(() => buildPermissionsConfig(permissionRows, isReadOnly), [isReadOnly]);

  const permissionTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.values(permissionRows)
      .flat()
      .forEach(({ Permission, Type }) => {
        map[Permission] = Type;
      });
    return map;
  }, []);

  const { ctx, renderFormElements } = useForm<PermissionFormValues>({
    config: Object.values(permissionsConfig).flat(),
    initialValues,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  const hasChanges = useMemo(
    () => JSON.stringify(ctx.values) !== JSON.stringify(initialValues),
    [ctx.values, initialValues],
  );

  useEffect(() => {
    onFormStateChange(hasChanges);
  }, [hasChanges]);

  useEffect(() => {
    onSearchChange(searchTerm.trim().length > 0);
  }, [searchTerm]);

  const groupStats = useMemo(() => {
    const stats: Record<string, { enabled: number; total: number; hasBooleans: boolean }> = {};

    Object.entries(permissionsConfig).forEach(([sectionName, fields]) => {
      const boolFields = fields.filter((f) => permissionTypeMap[String(f.fieldName)] === 'boolean');
      const total = boolFields.length;
      const enabled = boolFields.filter((f) => ctx.values[String(f.fieldName)] === true).length;
      stats[sectionName] = { enabled, total, hasBooleans: total > 0 };
    });

    return stats;
  }, [permissionsConfig, ctx.values, permissionTypeMap]);

  const enableAllInGroup = (sectionName: string) => {
    const fields = permissionsConfig[sectionName] || [];
    const updates: Record<string, boolean> = {};
    fields.forEach((f) => {
      if (permissionTypeMap[String(f.fieldName)] === 'boolean') {
        updates[String(f.fieldName)] = true;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  const disableAllInGroup = (sectionName: string) => {
    const fields = permissionsConfig[sectionName] || [];
    const updates: Record<string, boolean> = {};
    fields.forEach((f) => {
      if (permissionTypeMap[String(f.fieldName)] === 'boolean') {
        updates[String(f.fieldName)] = false;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  const enableAll = () => {
    const updates: Record<string, boolean> = {};
    Object.values(permissionsConfig)
      .flat()
      .forEach((f) => {
        if (permissionTypeMap[String(f.fieldName)] === 'boolean') {
          updates[String(f.fieldName)] = true;
        }
      });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  useImperativeHandle(ref, () => ({ enableAll }));

  const filteredPermissionsConfig = useMemo(() => {
    if (!searchTerm.trim()) return permissionsConfig;

    const lower = searchTerm.toLowerCase();
    const filtered: Record<string, FieldData<PermissionFormValues>[]> = {};

    Object.entries(permissionsConfig).forEach(([sectionName, fields]) => {
      const sectionMatches = sectionName.toLowerCase().includes(lower);
      const matchingFields = fields.filter((field) => {
        const label = String(field.label?.(ctx) ?? '');
        return label.toLowerCase().includes(lower);
      });

      if (sectionMatches || matchingFields.length > 0) {
        filtered[sectionName] = sectionMatches ? fields : matchingFields;
      }
    });

    return filtered;
  }, [permissionsConfig, searchTerm]);

  const hasFilteredResults = Object.keys(filteredPermissionsConfig).length > 0;

  return (
    <form onSubmit={ctx.handleSubmit} id="accountPermissionsForm" className={classes('formContainer')}>
      <Box mb="md" base="8" flx="col" className={classes('searchBox')}>
        <TextField
          name="permissionSearch"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlineShadow"
          elementLeft={
            <div className={classes('searchIconWrapper')}>
              <MaterialSymbol icon="search" size="24px" />
            </div>
          }
        />
      </Box>

      {!hasFilteredResults && searchTerm.trim() ? (
        <div className={classes('emptyStateWrapper')}>
          <Box flx="col" ai="center" jc="center" gap="sm">
            <MaterialSymbol icon="manage_search" size="90px" color="rgba(45, 45, 45, 0.2)" />
            <Title level="4">{t('noResults')}</Title>
          </Box>
        </div>
      ) : (
        <CollapsibleList>
          {Object.entries(filteredPermissionsConfig).map(([sectionName, sectionFields]) => {
            const stats = groupStats[sectionName] ?? { enabled: 0, total: 0, hasBooleans: false };
            const allEnabled = stats.hasBooleans && stats.enabled === stats.total;
            const isFiltering = searchTerm.trim().length > 0;

            return (
              <CollapsibleList.Item key={sectionName} defaultOpen={false} open={isFiltering}>
                <CollapsibleList.Trigger>
                  <Box flx="row" jc="sb" ai="center" grow="1">
                    <Box ai="center" gapSize="12px">
                      <Title level="5">{formatSectionName(sectionName)}</Title>
                      {stats.hasBooleans && (
                        <BodyText weight="rg" className={classes('groupStatsText')}>
                          ({stats.enabled}/{stats.total})
                        </BodyText>
                      )}
                    </Box>
                    {!isFiltering && stats.hasBooleans && !isReadOnly && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Box gap="sm">
                          {allEnabled ? (
                            <UtilButton size="medium" onClick={() => disableAllInGroup(sectionName)}>
                              {t('disableAll')}
                            </UtilButton>
                          ) : (
                            <UtilButton size="medium" onClick={() => enableAllInGroup(sectionName)}>
                              {t('enableAll')}
                            </UtilButton>
                          )}
                        </Box>
                      </div>
                    )}
                  </Box>
                </CollapsibleList.Trigger>

                <CollapsibleList.Content>
                  <Box flx="col" gap="md" base="8">
                    {renderFormElements({
                      config: sectionFields,
                      override: (fieldData) => {
                        const name = String(fieldData.fieldName);
                        const isBool = permissionTypeMap[name] === 'boolean';
                        return (
                          <label className={classes('permissionLabel')} htmlFor={isBool ? `checkbox-${name}` : name}>
                            <BodyText className={classes('permissionLabelText')} weight="md">
                              {fieldData.label}
                            </BodyText>
                            <div className={classes('inputWrapper')}>{fieldData.template}</div>
                          </label>
                        );
                      },
                    })}
                  </Box>
                </CollapsibleList.Content>
              </CollapsibleList.Item>
            );
          })}
        </CollapsibleList>
      )}
    </form>
  );
};

export default AccountPermissionsForm;
